import type { Stats } from 'fs';

import type Database from 'better-sqlite3';

import type { FileRecord, HashState } from '../types';

export interface UpsertFileParams {
  relPath: string;
  displayPath: string;
  stats: Stats;
  isSymlink: boolean;
  symlinkTarget: string | null;
}

export interface HashResult {
  id: number;
  contentSha256: string;
  serviceFileHash: string;
  hashOwner: string;
  hashStartedAt: number;
}

export class FilesRepo {
  private readonly selectByRelPath = this.db.prepare(
    'SELECT * FROM files WHERE rel_path = ?',
  );

  private readonly insertStmt = this.db.prepare(
    `INSERT INTO files (
      rel_path,
      display_path,
      size,
      mtime_ns,
      mode,
      ino,
      dev,
      is_symlink,
      target,
      content_sha256,
      service_file_hash,
      last_seen_ts,
      hash_state,
      hash_owner,
      hash_lease_expires_at,
      hash_started_at
    ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, NULL, NULL, ?, ?, NULL, NULL, NULL)`,
  );

  private readonly updateStmt = this.db.prepare(
    `UPDATE files SET
      display_path = ?,
      size = ?,
      mtime_ns = ?,
      mode = ?,
      ino = ?,
      dev = ?,
      is_symlink = ?,
      target = ?,
      content_sha256 = ?,
      service_file_hash = ?,
      last_seen_ts = ?,
      hash_state = ?,
      hash_owner = CASE WHEN ? = 'hashing' THEN hash_owner ELSE NULL END,
      hash_lease_expires_at = CASE WHEN ? = 'hashing' THEN hash_lease_expires_at ELSE NULL END,
      hash_started_at = CASE WHEN ? = 'hashing' THEN hash_started_at ELSE NULL END
    WHERE id = ?`,
  );

  private readonly markMissingStmt = this.db.prepare(
    `UPDATE files
     SET hash_state = 'missing', content_sha256 = NULL, service_file_hash = NULL, last_seen_ts = ?,
         hash_owner = NULL, hash_lease_expires_at = NULL, hash_started_at = NULL
     WHERE rel_path = ?`,
  );

  private readonly markMissingPrefixStmt = this.db.prepare(
    `UPDATE files
     SET hash_state = 'missing', content_sha256 = NULL, service_file_hash = NULL,
         hash_owner = NULL, hash_lease_expires_at = NULL, hash_started_at = NULL
     WHERE (rel_path = ? OR rel_path LIKE (? || '/%')) AND hash_state != 'missing'`,
  );

  private readonly markDirtyStmt = this.db.prepare(
    `UPDATE files
     SET hash_state = 'dirty', last_seen_ts = ?,
         hash_owner = NULL, hash_lease_expires_at = NULL, hash_started_at = NULL
     WHERE rel_path = ?`,
  );

  private readonly claimDirtyStmt = this.db.prepare(
    `WITH candidates AS (
       SELECT id
       FROM files
       WHERE hash_state = 'dirty'
       ORDER BY last_seen_ts ASC, id ASC
       LIMIT @limit
     )
     UPDATE files
     SET hash_state = 'hashing',
         hash_owner = @owner,
         hash_lease_expires_at = @lease_expires_at,
         hash_started_at = @now
     WHERE id IN candidates
     RETURNING *`,
  );

  private readonly markDirtyByIdStmt = this.db.prepare(
    `UPDATE files
     SET hash_state = 'dirty', hash_owner = NULL, hash_lease_expires_at = NULL, hash_started_at = NULL
     WHERE id = ?`,
  );

  private readonly applyHashesStmt = this.db.prepare(
    `UPDATE files
     SET content_sha256 = ?, service_file_hash = ?, hash_state = 'clean',
         hash_owner = NULL, hash_lease_expires_at = NULL, hash_started_at = NULL
     WHERE id = ?
       AND hash_state = 'hashing'
       AND hash_owner = ?
       AND hash_started_at = ?`,
  );

  private readonly requeueExpiredHashingStmt = this.db.prepare(
    `UPDATE files
     SET hash_state = 'dirty', hash_owner = NULL, hash_lease_expires_at = NULL, hash_started_at = NULL
     WHERE hash_state = 'hashing'
       AND hash_lease_expires_at IS NOT NULL
       AND hash_lease_expires_at <= ?`,
  );

  private readonly resetHashingStmt = this.db.prepare(
    `UPDATE files
     SET hash_state = 'dirty', hash_owner = NULL, hash_lease_expires_at = NULL, hash_started_at = NULL
     WHERE hash_state = 'hashing'`,
  );

  private readonly selectCleanSnapshotStmt = this.db.prepare(
    `SELECT rel_path, service_file_hash, size
     FROM files
     WHERE hash_state = 'clean' AND service_file_hash IS NOT NULL
     ORDER BY rel_path ASC`,
  );

  private readonly totalsStmt = this.db.prepare(
    `SELECT COUNT(*) AS files_count, COALESCE(SUM(size), 0) AS total_size
     FROM files
     WHERE hash_state = 'clean' AND service_file_hash IS NOT NULL`,
  );

  private readonly markMissingBeforeStmt = this.db.prepare(
    `UPDATE files
     SET hash_state = 'missing', content_sha256 = NULL, service_file_hash = NULL,
         hash_owner = NULL, hash_lease_expires_at = NULL, hash_started_at = NULL
     WHERE last_seen_ts < ? AND hash_state != 'missing'`,
  );

  private readonly countByStateStmt = this.db.prepare(
    'SELECT COUNT(*) as count FROM files WHERE hash_state = ?',
  );

  constructor(private readonly db: Database.Database) {}

  getByRelPath(relPath: string): FileRecord | undefined {
    return this.selectByRelPath.get(relPath) as FileRecord | undefined;
  }

  upsertFromStat(params: UpsertFileParams): HashState {
    const now = Date.now();
    const { relPath, displayPath, stats, isSymlink, symlinkTarget } = params;
    const existing = this.getByRelPath(relPath);
    const mtimeNs = Math.trunc(stats.mtimeMs * 1e6);
    const ino = typeof stats.ino === 'number' ? String(stats.ino) : null;
    const dev = typeof stats.dev === 'number' ? String(stats.dev) : null;
    const mode = typeof stats.mode === 'number' ? stats.mode : null;
    const isSymlinkInt = isSymlink ? 1 : 0;

    if (!existing) {
      this.insertStmt.run(
        relPath,
        displayPath,
        stats.size,
        mtimeNs,
        mode,
        ino,
        dev,
        isSymlinkInt,
        symlinkTarget,
        now,
        'dirty',
      );
      return 'dirty';
    }

    let nextState: HashState = existing.hash_state as HashState;
    let nextContent: string | null = existing.content_sha256;
    let nextServiceHash: string | null = existing.service_file_hash;

    const changed =
      existing.size !== stats.size ||
      existing.mtime_ns !== mtimeNs ||
      existing.mode !== mode ||
      existing.ino !== ino ||
      existing.dev !== dev ||
      existing.is_symlink !== isSymlinkInt ||
      existing.target !== symlinkTarget;

    if (changed || existing.hash_state === 'missing') {
      nextState = 'dirty';
      nextContent = null;
      nextServiceHash = null;
    }

    this.updateStmt.run(
      displayPath,
      stats.size,
      mtimeNs,
      mode,
      ino,
      dev,
      isSymlinkInt,
      symlinkTarget,
      nextContent,
      nextServiceHash,
      now,
      nextState,
      nextState,
      nextState,
      nextState,
      existing.id,
    );

    return nextState;
  }

  markMissing(relPath: string): number {
    const now = Date.now();
    const result = this.markMissingStmt.run(now, relPath);
    return result.changes ?? 0;
  }

  markMissingByPrefix(prefix: string): number {
    const result = this.markMissingPrefixStmt.run(prefix, prefix);
    return result.changes ?? 0;
  }

  markDirty(relPath: string): void {
    const now = Date.now();
    this.markDirtyStmt.run(now, relPath);
  }

  markMissingBefore(timestamp: number): number {
    const result = this.markMissingBeforeStmt.run(timestamp);
    return result.changes ?? 0;
  }

  claimDirty(limit: number, owner: string, leaseMs: number): FileRecord[] {
    if (limit <= 0) {
      return [];
    }
    const now = Date.now();
    return this.claimDirtyStmt.all({
      limit,
      owner,
      lease_expires_at: now + leaseMs,
      now,
    }) as FileRecord[];
  }

  markDirtyByIds(ids: number[]): void {
    if (!ids.length) return;
    const tx = this.db.transaction((batch: number[]) => {
      for (const id of batch) {
        this.markDirtyByIdStmt.run(id);
      }
    });
    tx(ids);
  }

  applyHashResults(results: HashResult[]): number {
    if (!results.length) return 0;
    let applied = 0;
    const tx = this.db.transaction((batch: HashResult[]) => {
      for (const {
        id,
        contentSha256,
        serviceFileHash,
        hashOwner,
        hashStartedAt,
      } of batch) {
        const result = this.applyHashesStmt.run(
          contentSha256,
          serviceFileHash,
          id,
          hashOwner,
          hashStartedAt,
        );
        if ((result.changes ?? 0) > 0) {
          applied += 1;
        }
      }
    });
    tx(results);
    return applied;
  }

  getCleanFilesForSnapshot(): Array<{
    rel_path: string;
    service_file_hash: string;
    size: number;
  }> {
    return this.selectCleanSnapshotStmt.all() as Array<{
      rel_path: string;
      service_file_hash: string;
      size: number;
    }>;
  }

  getTotalsForSnapshot(): { filesCount: number; totalSize: number } {
    const row = this.totalsStmt.get() as
      | { files_count: number; total_size: number }
      | undefined;
    return {
      filesCount: row?.files_count ?? 0,
      totalSize: row?.total_size ?? 0,
    };
  }

  countByState(state: HashState): number {
    const row = this.countByStateStmt.get(state) as
      | { count: number }
      | undefined;
    return row?.count ?? 0;
  }

  requeueExpiredHashing(now: number): number {
    const result = this.requeueExpiredHashingStmt.run(now);
    return result.changes ?? 0;
  }

  resetHashingStates(): number {
    const result = this.resetHashingStmt.run();
    return result.changes ?? 0;
  }
}
